import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Phone,
  Mail,
  Globe,
  Linkedin,
  Sparkles,
  Target,
  Users,
  MessageCircle,
  ShieldCheck,
  BookOpen,
  Compass,
  Zap,
  Heart,
  TrendingUp,
} from "lucide-react";
import SEO from "./SEO";

/**
 * Sebyx Frontline Elite Playbook — full print-ready ~40-page PDF companion
 * to the 2-day retail sales course. A4 portrait, dark theme, on-brand.
 */
export default function FrontlinePlaybook() {
  return (
    <main
      data-testid="frontline-playbook-page"
      className="min-h-screen bg-sebyx-bg text-sebyx-text font-body"
    >
      <SEO
        title="Frontline Elite Playbook | Premium Retail Sales Scripts"
        description="Turn showroom walk-ins into lifetime clients. 40-page printed playbook with behavioral sales frameworks and 60+ exact verbal scripts for premium and high-end retail."
        path="/frontline-playbook"
      />
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <ArrowLeft size={16} className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform" />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
              Frontline Elite · Playbook
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="playbook-print-btn"
              className="px-5 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
            >
              Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <div className="print:hidden max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted leading-relaxed">
          Tip · Print dialog: <span className="text-sebyx-accent">Layout = Portrait</span>,
          {" "}Margins = <span className="text-sebyx-accent">None</span>, Scale = 100%, Background graphics = <span className="text-sebyx-accent">On</span>, Headers/footers = <span className="text-sebyx-accent">Off</span>.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 print:p-0 print:max-w-none">
        <Cover />
        <Foreword />
        <HowToUse />
        <Toc />

        {MODULES.map((m, idx) => (
          <React.Fragment key={m.number}>
            <ModuleCover module={m} />
            {m.lessons.map((l, li) => (
              <LessonPage
                key={`${m.number}-${li}`}
                module={m}
                lesson={l}
                lessonIndex={li}
              />
            ))}
          </React.Fragment>
        ))}

        <ScorecardPage />
        <CertificationPage />
        <AboutSebyx />
      </div>
    </main>
  );
}

/* ---------------- Layout ---------------- */
function Page({ pageNumber, children, dense }) {
  return (
    <section
      data-testid={`playbook-p-${pageNumber}`}
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0 print:break-after-page"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: dense ? "12mm 14mm" : "16mm 16mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      <div className="relative h-full flex flex-col">{children}</div>
    </section>
  );
}

function PageHeader({ modLabel, modTitle }) {
  return (
    <div className="flex items-center justify-between pb-3 border-b border-sebyx-border mb-6">
      <div className="flex items-center gap-2">
        <span className="block w-2 h-2 bg-sebyx-accent" />
        <span className="font-display text-lg font-semibold tracking-tight">
          Sebyx
        </span>
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted ml-2">
          Frontline Elite · Playbook
        </span>
      </div>
      {modLabel && (
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted">
          {modLabel} · {modTitle}
        </span>
      )}
    </div>
  );
}

function PageFooter({ n }) {
  return (
    <div className="mt-auto pt-3 border-t border-sebyx-border flex items-center justify-between text-sebyx-muted font-mono text-[8px] tracking-[0.25em] uppercase">
      <span>© Sebyx · contact@sebyx.com · www.sebyx.com</span>
      <span>Page {n} · Brand of Serio Healthcare</span>
    </div>
  );
}

/* ---------------- Front matter ---------------- */
function Cover() {
  return (
    <Page pageNumber={1} dense>
      <div className="absolute inset-0 sebyx-grid-bg opacity-50 pointer-events-none" />
      <div className="relative h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="block w-3 h-3 bg-sebyx-accent" />
            <span className="font-display text-2xl font-semibold tracking-tight">
              Sebyx
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-muted ml-2">
              Revenue Architecture
            </span>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-muted">
            Ed. 01 · 2026
          </span>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-8">
            <span className="block w-14 h-px bg-sebyx-accent" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
              Frontline Elite · The 2-Day Retail Sales Playbook
            </span>
          </div>
          <h1 className="font-display text-6xl font-semibold tracking-tighter leading-[1.02] text-sebyx-text">
            Turn your{" "}
            <span className="text-sebyx-accent italic font-light">
              first retail job
            </span>{" "}
            into a{" "}
            <span className="text-sebyx-accent italic font-light">career</span>.
          </h1>
          <p className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-sebyx-muted">
            An entry-level field manual for ambitious young salespeople in
            India's high-end retail — luxury, jewellery, apparel, lingerie,
            electronics, beauty, and beyond.
          </p>

          <div className="mt-14 grid grid-cols-4 gap-px bg-sebyx-border border border-sebyx-border max-w-2xl">
            {[
              { k: "8", v: "Modules" },
              { k: "40+", v: "Pages" },
              { k: "60+", v: "Scripts & drills" },
              { k: "2 days", v: "Live cohort" },
            ].map((s, i) => (
              <div key={i} className="bg-sebyx-bg p-3 flex flex-col">
                <span className="font-display text-xl font-semibold text-sebyx-text">
                  {s.k}
                </span>
                <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-sebyx-muted">
                  {s.v}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-sebyx-border">
          <div className="grid grid-cols-2 gap-4 text-sebyx-muted font-mono text-[9px] tracking-[0.25em] uppercase">
            <div>
              <div>By</div>
              <div className="text-sebyx-text font-display text-base tracking-tight normal-case font-medium mt-1">
                Mandar Prabhudesai
              </div>
              <div className="normal-case">
                Founder · Sebyx · 18+ years in Indian B2B & retail sales
              </div>
            </div>
            <div className="text-right">
              <div>Sebyx Academy</div>
              <div className="text-sebyx-text font-display text-base tracking-tight normal-case font-medium mt-1">
                contact@sebyx.com
              </div>
              <div className="normal-case">+91 91084 36667 · www.sebyx.com</div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Foreword() {
  return (
    <Page pageNumber={2}>
      <PageHeader modLabel="Front Matter" modTitle="Foreword" />
      <div className="flex items-center gap-3 mb-4">
        <span className="block w-8 h-px bg-sebyx-accent" />
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
          A Note from Mandar
        </span>
      </div>
      <h2 className="font-display text-3xl font-semibold tracking-tighter leading-[1.05] mb-6">
        You are already in the room. Now learn to own it.
      </h2>
      <div className="space-y-4 text-[12px] text-sebyx-text/90 leading-relaxed max-w-3xl">
        <p>
          Eighteen years ago, I stood on a sales floor for the first time. No
          MBA, no playbook, no mentor. Just a target, a walk-in, and a
          catalogue I had to make sense of. Everything I learned since then
          — how to open a conversation with a stranger, how to read a buyer,
          how to close without pressuring, how to earn a customer who comes
          back and refers three friends — I learned the hard way, one
          uncomfortable interaction at a time.
        </p>
        <p>
          This playbook exists so you don't have to. If you have just landed
          your first job on the floor of a luxury store, a lingerie boutique,
          a jewellery counter, or a premium electronics showroom — congrats.
          You are already in a room most young Indians will never step into.
          Now you have a choice: treat it as a job, or treat it as an
          apprenticeship.
        </p>
        <p>
          The salespeople who treat it as an apprenticeship become the ones
          customers ask for by name. They earn twice as much through
          commissions and tips. They get promoted to store manager in 2 years,
          area manager in 4, and start their own boutique in 7. The ones who
          treat it as a job stay a rep for 20 years, watching younger people
          pass them.
        </p>
        <p>
          This playbook, and the 2-day Sebyx Frontline Elite programme it
          accompanies, is a decision to be in the first camp.
        </p>
        <p className="pt-3 text-sebyx-accent">
          Read it slowly. Practise the drills. Cross out what doesn't work in
          your context, and note in the margins what does. That's how a
          playbook becomes yours.
        </p>
        <p className="pt-6 font-display text-lg text-sebyx-text">
          — Mandar Prabhudesai
        </p>
        <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-sebyx-muted">
          Founder & Strategic Architect · Sebyx
        </p>
      </div>
      <PageFooter n={2} />
    </Page>
  );
}

function HowToUse() {
  return (
    <Page pageNumber={3}>
      <PageHeader modLabel="Front Matter" modTitle="How to Use" />
      <SectionEyebrow>How to Use This Playbook</SectionEyebrow>
      <h2 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mb-6 leading-tight">
        Three ways to work through Frontline Elite.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {[
          {
            t: "1. Solo self-study",
            b: "Read one module per week. Do the drills at home in front of a mirror or with a friend. Use the scorecard at the end of each module to grade yourself. Ideal for anyone unable to attend a live cohort.",
          },
          {
            t: "2. Cohort companion",
            b: "Attend the 2-day live Sebyx cohort (weekend format). This playbook is your workbook — write in the margins, mark up the scripts, tick off the drills. Take it back to the store on Monday.",
          },
          {
            t: "3. In-house team",
            b: "Your store manager runs one module per team huddle (15-20 min). Read section, do drill, apply on the floor that day. Great for a 6-week rollout in a store or chain.",
          },
        ].map((c, i) => (
          <div key={i} className="border border-sebyx-border bg-sebyx-surface/40 p-3">
            <h3 className="font-display text-sm font-semibold text-sebyx-text mb-2">
              {c.t}
            </h3>
            <p className="text-[11px] text-sebyx-muted leading-snug">{c.b}</p>
          </div>
        ))}
      </div>

      <SectionEyebrow>What Each Module Contains</SectionEyebrow>
      <ul className="space-y-2 text-[12px] text-sebyx-text/90 leading-relaxed max-w-3xl mt-3">
        {[
          "A short story or case — a real (anonymised) example of how the topic plays out on an Indian retail floor.",
          "The framework — a simple, memorable model you can use tomorrow.",
          "The scripts — exact words to say and words to avoid.",
          "The drills — a role-play or self-assessment to practise.",
          "The scorecard — a self-grade to track your progress.",
        ].map((x, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
            {x}
          </li>
        ))}
      </ul>

      <div className="mt-6 border-l-2 border-sebyx-accent bg-sebyx-surface/40 pl-4 py-3">
        <p className="text-[11.5px] leading-relaxed text-sebyx-text">
          <span className="text-sebyx-accent">Golden rule:</span> Reading
          without practising is entertainment. Practising without measuring is
          a hobby. Practising and measuring is a career.
        </p>
      </div>

      <PageFooter n={3} />
    </Page>
  );
}

function Toc() {
  return (
    <Page pageNumber={4}>
      <PageHeader modLabel="Front Matter" modTitle="Contents" />
      <SectionEyebrow>Table of Contents</SectionEyebrow>
      <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">
        The 8 modules of Frontline Elite.
      </h2>
      <div className="border-t border-sebyx-border">
        {MODULES.map((m) => (
          <div key={m.number} className="border-b border-sebyx-border py-3 grid grid-cols-12 gap-3 items-baseline">
            <div className="col-span-1 font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-accent">
              M{m.number}
            </div>
            <div className="col-span-8">
              <div className="font-display text-base font-medium text-sebyx-text">
                {m.title}
              </div>
              <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-sebyx-muted mt-0.5">
                {m.tagline}
              </div>
            </div>
            <div className="col-span-3 text-right text-[10px] text-sebyx-muted font-mono tracking-[0.15em]">
              {m.lessons.length} lessons
            </div>
          </div>
        ))}
      </div>
      <PageFooter n={4} />
    </Page>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="block w-8 h-px bg-sebyx-accent" />
      <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
        {children}
      </span>
    </div>
  );
}

/* ---------------- Module rendering ---------------- */
let PAGE_COUNTER = 4;

function ModuleCover({ module }) {
  PAGE_COUNTER += 1;
  const Icon = module.icon;
  return (
    <Page pageNumber={PAGE_COUNTER}>
      <PageHeader modLabel={`Module ${module.number}`} modTitle={module.title} />
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-14 h-px bg-sebyx-accent" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
            Module {module.number}
          </span>
        </div>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 border border-sebyx-accent flex items-center justify-center shrink-0">
            <Icon size={22} className="text-sebyx-accent" />
          </div>
          <h1 className="font-display text-5xl font-semibold tracking-tighter leading-[1.02] flex-1">
            {module.title}
          </h1>
        </div>
        <p className="text-base leading-relaxed text-sebyx-muted max-w-2xl mb-8">
          {module.tagline}
        </p>
        <div className="border border-sebyx-border bg-sebyx-surface/40 p-4">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent block mb-3">
            What you'll learn
          </span>
          <ul className="space-y-2 text-[12px] text-sebyx-text/90 leading-snug">
            {module.lessons.map((l, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                <span>
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-sebyx-accent mr-2">
                    L{i + 1}
                  </span>
                  {l.title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <PageFooter n={PAGE_COUNTER} />
    </Page>
  );
}

function LessonPage({ module, lesson, lessonIndex }) {
  PAGE_COUNTER += 1;
  return (
    <Page pageNumber={PAGE_COUNTER}>
      <PageHeader modLabel={`M${module.number}.L${lessonIndex + 1}`} modTitle={module.title} />

      <SectionEyebrow>Lesson {lessonIndex + 1} · {module.tagline}</SectionEyebrow>
      <h2 className="font-display text-2xl lg:text-[26px] font-semibold tracking-tight mb-4 leading-tight">
        {lesson.title}
      </h2>

      <div className="space-y-4 text-[11.5px] text-sebyx-text/90 leading-relaxed">
        {lesson.opener && (
          <div className="border-l-2 border-sebyx-accent bg-sebyx-surface/40 pl-4 py-2 italic">
            {lesson.opener}
          </div>
        )}

        {lesson.body && lesson.body.map((paragraph, i) => (
          <p key={i}>{paragraph}</p>
        ))}

        {lesson.framework && (
          <div>
            <SectionEyebrow>The Framework</SectionEyebrow>
            <div className="border border-sebyx-border bg-sebyx-surface/30 p-3">
              <h4 className="font-display text-sm font-semibold mb-2">
                {lesson.framework.name}
              </h4>
              <ul className="space-y-1.5">
                {lesson.framework.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px]">
                    <span className="font-mono text-[9px] text-sebyx-accent mt-0.5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>
                      {p.h && <span className="text-sebyx-text font-medium">{p.h} — </span>}
                      <span className="text-sebyx-text/90">{p.b}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {lesson.scripts && (
          <div>
            <SectionEyebrow>Scripts · Say This / Not That</SectionEyebrow>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-green-800/40 bg-green-900/10 p-2.5">
                <span className="font-mono text-[8.5px] tracking-[0.25em] uppercase text-green-400 block mb-1.5">
                  ✓ Say
                </span>
                <ul className="space-y-1.5 text-[10.5px] text-sebyx-text/90">
                  {lesson.scripts.say.map((s, i) => (
                    <li key={i}>"{s}"</li>
                  ))}
                </ul>
              </div>
              <div className="border border-red-800/40 bg-red-900/10 p-2.5">
                <span className="font-mono text-[8.5px] tracking-[0.25em] uppercase text-red-400 block mb-1.5">
                  ✗ Never say
                </span>
                <ul className="space-y-1.5 text-[10.5px] text-sebyx-text/90">
                  {lesson.scripts.dont.map((s, i) => (
                    <li key={i}>"{s}"</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {lesson.drill && (
          <div>
            <SectionEyebrow>Drill · Practise Today</SectionEyebrow>
            <div className="border border-sebyx-border bg-sebyx-bg p-3">
              <h4 className="font-display text-sm font-semibold mb-1.5">
                {lesson.drill.name}
              </h4>
              <p className="text-[11px] text-sebyx-muted leading-snug">
                {lesson.drill.desc}
              </p>
              {lesson.drill.steps && (
                <ol className="mt-2 space-y-1 text-[11px] text-sebyx-text/90 list-decimal pl-4">
                  {lesson.drill.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}

        {lesson.scorecard && (
          <div>
            <SectionEyebrow>Self-Score · Grade Yourself 1–5</SectionEyebrow>
            <div className="grid grid-cols-1 gap-1.5">
              {lesson.scorecard.map((s, i) => (
                <div key={i} className="border border-sebyx-border p-2 flex items-center justify-between text-[10.5px]">
                  <span className="text-sebyx-text/90">{s}</span>
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-sebyx-muted">
                    1 · 2 · 3 · 4 · 5
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <PageFooter n={PAGE_COUNTER} />
    </Page>
  );
}

/* ---------------- Back matter ---------------- */
function ScorecardPage() {
  PAGE_COUNTER += 1;
  return (
    <Page pageNumber={PAGE_COUNTER}>
      <PageHeader modLabel="Appendix A" modTitle="30-60-90 Scorecard" />
      <SectionEyebrow>The Frontline Elite 30-60-90 Scorecard</SectionEyebrow>
      <h2 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mb-4 leading-tight">
        Measure your first 90 days on the floor.
      </h2>
      <p className="text-[11.5px] text-sebyx-muted leading-relaxed mb-4 max-w-3xl">
        Print this page. Track your numbers every Sunday evening. If you can't
        measure it, you can't improve it. If you can measure it and improve it
        every week, you'll be the top salesperson in your store within a year
        — guaranteed.
      </p>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { p: "Day 30", g: ["Learned every SKU in your section", "Handled 100+ walk-ins", "First unassisted close"] },
          { p: "Day 60", g: ["3-star rating on customer feedback (or higher)", "First upsell close", "Built a personal book of 25+ customers"] },
          { p: "Day 90", g: ["First repeat customer visit", "First referral close", "First cross-category close"] },
        ].map((c, i) => (
          <div key={i} className="border border-sebyx-border bg-sebyx-surface/30 p-3">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent block mb-2">
              {c.p}
            </span>
            <ul className="space-y-1.5">
              {c.g.map((g, j) => (
                <li key={j} className="flex items-start gap-2 text-[10.5px] text-sebyx-text/90">
                  <span className="block w-3 h-3 border border-sebyx-border shrink-0 mt-0.5" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <SectionEyebrow>Weekly Numbers to Track</SectionEyebrow>
      <div className="border border-sebyx-border">
        <div className="grid grid-cols-6 gap-px bg-sebyx-border text-[9.5px] font-mono tracking-[0.15em] uppercase text-sebyx-muted">
          {["Week", "Walk-ins", "Conversations", "Trials", "Closes", "₹ Sold"].map((h) => (
            <div key={h} className="bg-sebyx-bg p-2 text-center">{h}</div>
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="grid grid-cols-6 gap-px bg-sebyx-border text-[10.5px]">
            {Array.from({ length: 6 }).map((__, j) => (
              <div key={j} className="bg-sebyx-bg p-2 text-center text-sebyx-muted">
                {j === 0 ? `W${i + 1}` : ""}
              </div>
            ))}
          </div>
        ))}
      </div>

      <PageFooter n={PAGE_COUNTER} />
    </Page>
  );
}

function CertificationPage() {
  PAGE_COUNTER += 1;
  return (
    <Page pageNumber={PAGE_COUNTER}>
      <PageHeader modLabel="Appendix B" modTitle="Certification" />
      <div className="flex-1 flex flex-col justify-center items-center text-center">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-14 h-px bg-sebyx-accent" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
            Sebyx Frontline Elite · Certification
          </span>
          <span className="block w-14 h-px bg-sebyx-accent" />
        </div>
        <h1 className="font-display text-5xl font-semibold tracking-tighter leading-[1.02] max-w-2xl">
          You are now Frontline Elite.
        </h1>
        <p className="mt-6 max-w-xl text-sm text-sebyx-muted leading-relaxed">
          Complete the 2-day live cohort + submit your first month's scorecard
          to earn the Sebyx Frontline Elite Certification — recognised by our
          partner retailers across Karnataka, Maharashtra, and Gujarat.
        </p>

        <div className="mt-10 grid grid-cols-2 gap-3 max-w-xl w-full">
          {[
            { h: "Sebyx Frontline Elite Badge", b: "Sharable on LinkedIn, WhatsApp, resume" },
            { h: "Alumni WhatsApp Group", b: "Peer network across India's top luxury floors" },
            { h: "Partner Store Referrals", b: "Priority intros to hiring managers" },
            { h: "Priority for Pro Cohort", b: "Discounted seat in Sebyx Master Hunter" },
          ].map((b, i) => (
            <div key={i} className="border border-sebyx-border bg-sebyx-surface/30 p-3 text-left">
              <div className="font-display text-sm font-semibold text-sebyx-text mb-1">
                {b.h}
              </div>
              <div className="text-[10.5px] text-sebyx-muted leading-snug">
                {b.b}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-muted">
          Certified by Mandar Prabhudesai · Founder · Sebyx
        </p>
      </div>
      <PageFooter n={PAGE_COUNTER} />
    </Page>
  );
}

function AboutSebyx() {
  PAGE_COUNTER += 1;
  return (
    <Page pageNumber={PAGE_COUNTER}>
      <PageHeader modLabel="Colophon" modTitle="About Sebyx" />
      <SectionEyebrow>About Sebyx</SectionEyebrow>
      <h2 className="font-display text-3xl font-semibold tracking-tighter mb-6 leading-tight">
        Where Frontline Elite fits.
      </h2>
      <p className="text-[12px] text-sebyx-text/90 leading-relaxed max-w-3xl mb-5">
        Sebyx is a revenue architecture practice founded by Mandar Prabhudesai
        after 18 years across India's most demanding B2B and retail sales
        environments. Sebyx offers a three-tier career ladder for people who
        take sales seriously as a craft.
      </p>

      <div className="grid grid-cols-3 gap-2 mb-6">
        {[
          {
            code: "Tier 01",
            name: "Frontline Elite",
            who: "Retail & entry-level sales",
            format: "2-day cohort · playbook · certificate",
          },
          {
            code: "Tier 02",
            name: "Master Hunter",
            who: "Mid-level B2B managers",
            format: "4-week cohort · certification · alumni",
          },
          {
            code: "Tier 03",
            name: "Executive Mentorship",
            who: "Founders & sales VPs",
            format: "1-on-1 · 30-day intensive · bespoke",
          },
        ].map((t, i) => (
          <div key={i} className="border border-sebyx-border bg-sebyx-surface/30 p-3">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent block">
              {t.code}
            </span>
            <div className="font-display text-base font-semibold text-sebyx-text mt-1">
              {t.name}
            </div>
            <div className="text-[10.5px] text-sebyx-muted mt-1">{t.who}</div>
            <div className="text-[10px] text-sebyx-muted mt-2 font-mono tracking-[0.15em]">
              {t.format}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-sebyx-accent bg-sebyx-surface/40 p-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="block w-6 h-px bg-sebyx-accent" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
            Talk to Sebyx
          </span>
        </div>
        <h3 className="font-display text-xl font-semibold text-sebyx-text mb-2">
          Ready for the live cohort?
        </h3>
        <p className="text-[11px] text-sebyx-text/90 leading-relaxed mb-3">
          Frontline Elite runs as a 2-day live cohort in Bengaluru, Mumbai, and
          Ahmedabad, plus in-house sessions for stores training 5+ staff at
          once. Reply with your name, city, and preferred date.
        </p>
        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <ContactCell icon={Phone} label="Direct" value="+91 91084 36667" />
          <ContactCell icon={Mail} label="Email" value="contact@sebyx.com" />
          <ContactCell icon={Globe} label="Web" value="www.sebyx.com" />
          <ContactCell
            icon={Linkedin}
            label="LinkedIn"
            value="/in/mandarprabhudesai"
          />
        </div>
      </div>

      <PageFooter n={PAGE_COUNTER} />
    </Page>
  );
}

function ContactCell({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-7 h-7 border border-sebyx-border bg-sebyx-bg flex items-center justify-center shrink-0">
        <Icon size={11} className="text-sebyx-accent" />
      </div>
      <div>
        <div className="font-mono text-[8px] tracking-[0.25em] uppercase text-sebyx-muted">
          {label}
        </div>
        <div className="font-display text-[12px] text-sebyx-text">{value}</div>
      </div>
    </div>
  );
}

/* ---------------- Content: 8 modules × 4 lessons ---------------- */
const MODULES = [
  {
    number: 1,
    title: "The Salesperson Mindset",
    tagline: "Why sales is a career, not a job.",
    icon: Compass,
    lessons: [
      {
        title: "Sales is a real career. Treat it like one.",
        opener: "The best-paid people in every luxury brand in India started on the sales floor. Almost none of them planned to stay there.",
        body: [
          "Most young Indians walk into their first retail job carrying an apology. \"I'm just doing this while I figure things out.\" \"It's temporary.\" \"My real career hasn't started yet.\" That apology leaks into every conversation with a customer — and customers can smell it in three seconds.",
          "Here's the truth. Sales is one of the last true meritocracies in India. Nobody cares which college you went to, which language you speak first, or who your parents know. If you can turn a walk-in into a customer and a customer into a fan, your income and your title will keep going up — for the rest of your life. If you can't, no MBA on earth will save you.",
          "This playbook is written on one assumption: you are not doing this while you figure things out. You are doing this because you have chosen it. The moment you decide sales is your career, everything else in this book starts working.",
        ],
        framework: {
          name: "The Three Levels of a Salesperson",
          points: [
            { h: "L1 · Order-Taker", b: "Waits for customers, answers questions, processes payment. Interchangeable. Paid the minimum." },
            { h: "L2 · Salesperson", b: "Approaches, discovers needs, matches product, closes. Valuable. Paid a commission." },
            { h: "L3 · Trusted Advisor", b: "Builds a book of repeat customers who ask for them by name. Irreplaceable. Paid a career." },
          ],
        },
        drill: {
          name: "The Career Commitment Drill",
          desc: "Take a blank sheet. Write down: 'In 5 years, I will be a __________ earning ₹__________ per month.' Fold it. Put it in your wallet. Look at it every morning before your shift.",
        },
        scorecard: [
          "I can articulate why I chose sales, in one sentence.",
          "I know the name of at least 3 senior people in my industry.",
          "I can name my income target for next year.",
        ],
      },
      {
        title: "The math of a great sales year.",
        body: [
          "Most young salespeople never do the math on their own career. If you did, you'd behave completely differently on Monday morning.",
          "Let's say your base is ₹18,000/month and your average commission is ₹4,000. Total: ₹22K/month or ₹2.64L/year. If you become a top-quartile salesperson, you can typically 2.5x commissions — that's ₹120K extra a year, without any promotion. If you get promoted to floor supervisor in year 3, add another ₹8-10K/month base. If you get promoted to store manager in year 5, you're at ₹60-80K/month plus a floor override.",
          "The compounding is real. Every skill you learn in Year 1 pays you every year for the next 40. That's why the two days you invest in this playbook are among the highest-return two days of your life.",
        ],
        framework: {
          name: "The Salesperson Income Compounding Curve",
          points: [
            { h: "Year 1", b: "Base + small commission. Learning phase. Focus: consistency + basics." },
            { h: "Year 2-3", b: "Commissions 2-3x base. Focus: closing rate + upsells + your first repeat customers." },
            { h: "Year 4-5", b: "Supervisor / floor lead. Focus: teaching juniors + running mini-teams + hitting store targets." },
            { h: "Year 6-8", b: "Store or category manager. Focus: P&L thinking + hiring + brand relationships." },
            { h: "Year 8+", b: "Area manager / franchise partner / own boutique. Focus: capital + brand + people." },
          ],
        },
        drill: {
          name: "Your 5-Year Income Ladder",
          desc: "Fill in each year of your income + role for the next 5 years. Be realistic, not modest.",
        },
        scorecard: [
          "I know the commission structure at my store, exactly.",
          "I've talked to a Year-5 person in my industry about their journey.",
          "I have a written 5-year income target.",
        ],
      },
      {
        title: "The order-taker vs. the salesperson.",
        body: [
          "Same store. Same product. Same walk-in. Two salespeople. One does ₹40K a day. The other does ₹15K. What is the difference?",
          "The difference is never about product knowledge. Order-takers know their catalogue better than salespeople. The difference is what they do with the customer's attention in the first 90 seconds.",
        ],
        scripts: {
          say: [
            "Welcome — is this your first time here?",
            "Are you shopping for yourself today, or for someone special?",
            "This is one of our newest pieces — may I show you why?",
          ],
          dont: [
            "Yes, may I help you? (Every store says this. Dead.)",
            "Just let me know if you need anything. (Translation: I've given up.)",
            "That's ₹12,999. (Answers the wrong question first.)",
          ],
        },
        drill: {
          name: "The 90-Second Reset",
          desc: "For 5 walk-ins tomorrow, use the 'is this your first time / who is it for / may I show you why' triplet. Track how many led to a real conversation. Compare to your normal opener.",
        },
      },
      {
        title: "The professional identity mirror.",
        body: [
          "Before you can sell anything to anyone, you have to sell yourself the idea that you belong on this floor. The single biggest gap between a top salesperson and everyone else is not skill — it's belief.",
          "Look in the mirror before every shift. Ask yourself one question: would I buy from me right now?",
          "If the answer is anything less than a clean yes — fix the shirt, fix the shoes, fix the smile, fix the posture. Do not walk onto the floor with a maybe.",
        ],
        drill: {
          name: "The Pre-Shift Mirror Check",
          desc: "For 30 days, before every shift, spend 60 seconds in front of a mirror running the checklist below.",
          steps: [
            "Uniform / clothes ironed and clean",
            "Shoes polished, laces tied",
            "Hair, teeth, breath, nails",
            "Smile — practise 3 versions (warm, curious, ready)",
            "Posture — shoulders back, chin level",
            "One line of confidence: \"I'm going to close today.\"",
          ],
        },
        scorecard: [
          "I do the mirror check before every shift.",
          "I have 3 clean uniforms in rotation, always ready.",
          "I know the version of me that customers respond to best.",
        ],
      },
    ],
  },
  {
    number: 2,
    title: "Professional Presence & Personal Brand",
    tagline: "How you're read in the first 5 seconds.",
    icon: Sparkles,
    lessons: [
      {
        title: "The first 5 seconds are decided before you speak.",
        body: [
          "A customer walks into your store. Before you have said a single word, they have already decided three things about you: are you competent, are you trustworthy, are you worth their time. Research on retail encounters shows this judgement happens in about 5 seconds — and it's very hard to reverse.",
          "In luxury, the judgement is even stricter. Customers buying at ₹15K+ don't just want a product. They want the experience of being served by someone who understands the product's world. Your presence has to match the price tag.",
        ],
        framework: {
          name: "The 5-Second Signal Stack",
          points: [
            { h: "Grooming", b: "Clean, ironed, polished. No stains, no scuff marks, no dirty nails." },
            { h: "Posture", b: "Standing tall, shoulders back, not slouching against the counter." },
            { h: "Face", b: "Ready smile, eye contact, not scrolling a phone." },
            { h: "Environment", b: "Your section is tidy, mirrors clean, product arranged." },
            { h: "Voice", b: "Steady, warm, unhurried. Not shrill or apologetic." },
          ],
        },
      },
      {
        title: "Dressing on-code for the store you work in.",
        body: [
          "Every store has a code — the invisible dress language its customers expect from staff. A watch boutique code is different from a lingerie boutique code, which is different from a jewellery counter code. Get the code right, and customers assume you know your product. Get it wrong, and they will not take you seriously — no matter how good you are.",
          "If your store gives you a uniform, wear it like it's tailored for you. If it doesn't, take the salary of the customer you serve and dress one notch below them — never above, never below by much.",
        ],
        drill: {
          name: "The Uniform Rotation",
          desc: "Own 3 clean sets. Rotate. Never wear the same thing twice in a row. Iron the night before, not the morning of.",
        },
        scorecard: [
          "I know what 'dressed correctly' looks like for my exact store.",
          "I have 3 rotation-ready outfits.",
          "My shoes are always polished and closed-toe.",
        ],
      },
      {
        title: "Voice, posture, and unhurried energy.",
        body: [
          "New salespeople talk too fast, too loud, and too much. Every one of those signals to the customer 'I need this sale' — which instantly reduces the price they're willing to pay.",
          "Watch the top luxury salesperson in any store: they speak slowly, pause often, and let the customer talk more than they do. This isn't a personality trait. It's a learned skill.",
        ],
        scripts: {
          say: [
            "(pause) Take your time. There's no rush.",
            "May I show you something?",
            "That's a beautiful question — let me explain.",
          ],
          dont: [
            "Sir sir sir this one is very best price please buy...",
            "This is the last piece, buy fast!",
            "Discount possible only today, madam!",
          ],
        },
        drill: {
          name: "The Slow-Down Drill",
          desc: "For one full shift, deliberately halve your speech speed. Add a 2-second pause before every reply. Notice how customers respond.",
        },
      },
      {
        title: "Building professional equity from Day 1.",
        body: [
          "Professional equity is the reputation you build with every colleague, manager, and customer you interact with. It compounds silently for years — and then explodes into promotions, referrals, and job offers.",
          "The salespeople with high professional equity by year 3 are the ones who: never took a shortcut, never spoke badly about a customer, never blamed a colleague, and never treated a small-ticket customer any differently from a big-ticket one.",
        ],
        framework: {
          name: "The 4 Rules of Professional Equity",
          points: [
            { h: "Reliable", b: "You show up on time, every time. Small commitments are kept." },
            { h: "Respectful", b: "Every customer is treated the same way — the ₹500 buyer and the ₹5L buyer." },
            { h: "Reflective", b: "You debrief after tough interactions. What could I have done better?" },
            { h: "Referable", b: "You leave every customer thinking: I'd send my friend to this person." },
          ],
        },
        scorecard: [
          "I have never spoken badly about a customer or colleague at work.",
          "I have shown up on time for every shift this month.",
          "My manager would give me a strong reference tomorrow.",
        ],
      },
    ],
  },
  {
    number: 3,
    title: "The First 30 Seconds",
    tagline: "The choreography of the greeting.",
    icon: Zap,
    lessons: [
      {
        title: "The 3-step greeting choreography.",
        body: [
          "The moment a customer walks through your door is the single most important moment of the entire interaction. Get it wrong, and you spend the next 20 minutes trying to recover. Get it right, and the sale is 40% closed before you've said a word about the product.",
          "Every top salesperson has the same 3-step choreography, whether they realise it or not.",
        ],
        framework: {
          name: "The 3-Step Choreography",
          points: [
            { h: "Notice", b: "Make eye contact within 3 seconds of them entering. Smile. Nod. Do NOT pounce." },
            { h: "Approach", b: "Wait 15-20 seconds. Approach at a 45° angle, not straight on. Leave 3-4 feet of space." },
            { h: "Open", b: "Ask an open question about who or what they're shopping for — not 'may I help you'." },
          ],
        },
        scripts: {
          say: [
            "Welcome. Please look around — I'll be right here if you'd like help.",
            "Is this your first visit to us, or have you been in before?",
            "Something specific in mind, or shopping the collection?",
          ],
          dont: [
            "Yes sir? (Puts pressure on them immediately.)",
            "What's your budget? (Never lead with money.)",
            "Everything is on discount today! (Sounds desperate.)",
          ],
        },
        drill: {
          name: "The 3-Step Practice",
          desc: "Tomorrow, walk through the choreography exactly as written for 10 walk-ins. Score yourself out of 10 after each. Which steps did you skip?",
        },
      },
      {
        title: "The 8 walk-in archetypes.",
        body: [
          "Customers rarely arrive as blank slates. They arrive as one of 8 recognisable patterns, and each one needs a slightly different opener. Learn to identify the pattern in the first 10 seconds, and you have a huge advantage.",
        ],
        framework: {
          name: "The 8 Archetypes",
          points: [
            { h: "The Gifter", b: "Looking for someone else. Nervous. Needs guidance. → Ask who and what occasion." },
            { h: "The Self-Rewarder", b: "Just got a bonus / promotion. Wants to celebrate. → Match their mood, don't rush." },
            { h: "The Browser", b: "Killing time. May become a real customer if treated well. → Never dismiss them." },
            { h: "The Returner", b: "Been here before. Wants to be remembered. → 'Welcome back — great to see you again.'" },
            { h: "The Researcher", b: "Has already Googled. Knows too much. → Add context and stories they can't find online." },
            { h: "The Impatient", b: "In a hurry. Knows what they want. → Show 2 options fast, close in 5 min." },
            { h: "The Indecisive", b: "Wants everything, buys nothing. → Narrow their choice to 2. Force a small commitment." },
            { h: "The Skeptic", b: "Suspects overpricing. → Lead with value, not price. Use social proof." },
          ],
        },
        drill: {
          name: "The Archetype Log",
          desc: "For a full week, log every walk-in you serve into one of the 8 archetypes. At week-end, review which archetype you close best and which you struggle with.",
        },
      },
      {
        title: "What NOT to say in the first 30 seconds.",
        body: [
          "The 30-second window is when new salespeople make their worst mistakes. These aren't small mistakes — each one directly kills conversion.",
        ],
        scripts: {
          say: [
            "Take your time — I'm right here when you'd like to see something.",
            "Would you like water / tea / coffee while you browse?",
            "May I show you what just came in this week?",
          ],
          dont: [
            "Ma'am / sir, what's your budget?",
            "This one is very expensive — you sure you want to see?",
            "That's a discount item — are you sure?",
            "You look like you're in a rush — should I make it quick?",
          ],
        },
        drill: {
          name: "The Killer Phrase Audit",
          desc: "Record yourself (mentally or on paper) for one full shift. Every time you use one of the 'never say' phrases above, note it. The goal: zero by week 2.",
        },
      },
      {
        title: "Silence is a sales tool.",
        body: [
          "New salespeople are terrified of silence. Experienced ones use it as their best tool.",
          "After you show a product, be quiet. After you state a price, be quiet. After you make a recommendation, be quiet. The first person to break the silence usually loses the negotiation.",
          "Silence signals confidence. Talking to fill silence signals that you doubt your own offer.",
        ],
        drill: {
          name: "The 5-Second Silence Rule",
          desc: "After every price statement or recommendation, count to 5 silently before speaking again. Let the customer respond first. Track results for one week.",
        },
        scorecard: [
          "I can hold a 5-second silence after stating a price.",
          "I know the 8 archetypes and can name my strongest and weakest.",
          "I've eliminated the 4 killer phrases from my vocabulary.",
        ],
      },
    ],
  },
  {
    number: 4,
    title: "Customer Psychology in Premium Retail",
    tagline: "Why people actually buy premium.",
    icon: Heart,
    lessons: [
      {
        title: "The 6 real reasons people buy premium.",
        body: [
          "Nobody actually needs a ₹40,000 handbag. Nobody strictly needs a ₹1L watch. And yet these products sell every day, all over India. Why?",
          "The answer is that premium purchases are almost never about the product's function. They are about what the product does for the buyer's identity, emotion, or story. If you understand which of the 6 real reasons is driving THIS customer, you can serve them ten times better than a salesperson who only talks about features.",
        ],
        framework: {
          name: "The 6 Real Reasons",
          points: [
            { h: "Self-reward", b: "\"I earned this.\" A promotion, a bonus, finishing a hard year." },
            { h: "Status signal", b: "\"I want people to know.\" Wearing the brand, showing the receipt, visibility." },
            { h: "Gift", b: "\"I want them to feel special.\" Romantic, familial, anniversary, apology." },
            { h: "Ritual", b: "\"I always buy this here.\" Loyalty to brand, store, or you." },
            { h: "Belonging", b: "\"I want to be part of this world.\" First luxury purchase, aspirational entry." },
            { h: "Sensory / craft", b: "\"I appreciate the making.\" The connoisseur — quality, materials, story." },
          ],
        },
        drill: {
          name: "The 'Which of the 6' Practice",
          desc: "For your next 20 walk-ins, silently guess which of the 6 reasons is driving them within 60 seconds. Ask a soft question to confirm. Track your hit rate.",
        },
      },
      {
        title: "The luxury buying decision tree.",
        body: [
          "Premium buying decisions follow a predictable emotional path. If you can guide the customer through the path, they buy. If you skip a step or push too hard, they leave.",
        ],
        framework: {
          name: "The Decision Tree",
          points: [
            { h: "Arousal", b: "They see something that catches their eye. Job: keep the spark alive." },
            { h: "Justification", b: "Their brain looks for reasons to say yes. Job: give them 3 strong ones." },
            { h: "Ownership fantasy", b: "They imagine using / wearing it. Job: help them try it, hold it, imagine it." },
            { h: "Doubt", b: "Right before the yes, they doubt. Job: don't push — reassure." },
            { h: "Commitment", b: "The purchase. Job: make it feel like the beginning, not the end." },
          ],
        },
      },
      {
        title: "Reading buyer signals — verbal and non-verbal.",
        body: [
          "Every customer sends signals about how close they are to buying. Reading these signals accurately is the difference between closing and losing.",
        ],
        framework: {
          name: "The Buying Signals Checklist",
          points: [
            { h: "They pick it up more than once", b: "High interest — offer to open the case." },
            { h: "They ask about care or warranty", b: "Very close — they're already imagining ownership." },
            { h: "They ask 'and what else…'", b: "Warming up — bring one more piece to complete the story." },
            { h: "They look at price and don't wince", b: "Green light — proceed to a soft close." },
            { h: "They ask 'is this available in…' ", b: "They want it — check stock or promise delivery." },
            { h: "They photograph it", b: "They want to check with someone — offer a WhatsApp." },
          ],
        },
      },
      {
        title: "Discretion-sensitive selling — lingerie, gifting, personal.",
        body: [
          "Some categories require far more sensitivity than others. Lingerie, intimate wellness, personal grooming, and high-emotion gifting (anniversaries, apologies, mourning) all fall into what Sebyx calls 'discretion-sensitive' selling.",
          "The rules are simple but often broken: never comment on the person, only on the product. Never ask a question that implies judgement. Never mention the customer by name loudly. Never lock eyes with a partner or friend accompanying the buyer.",
        ],
        scripts: {
          say: [
            "Would you like to try this in a private area?",
            "I can bring 3 sizes to the trial room — take your time.",
            "This is one of our most popular gift choices — would you like it wrapped?",
          ],
          dont: [
            "Are you sure this is your size?",
            "Is this for your wife or your girlfriend? (Ask 'is this a gift?' instead.)",
            "You'll definitely need a larger size, ma'am. (Never comment on body.)",
          ],
        },
        drill: {
          name: "The Discretion Audit",
          desc: "If your category is discretion-sensitive, review every phrase you used in the last week. Circle any that could feel invasive. Replace them with neutral alternatives.",
        },
        scorecard: [
          "I can identify all 6 buying reasons.",
          "I can name 3 buying signals from memory.",
          "I have a neutral script for every discretion-sensitive moment in my category.",
        ],
      },
    ],
  },
  {
    number: 5,
    title: "Consultative Selling — Ask, Don't Push",
    tagline: "Turn 'may I help you' into a real conversation.",
    icon: MessageCircle,
    lessons: [
      {
        title: "The 4-question discovery framework.",
        body: [
          "The single biggest change you can make to your sales is to stop showing products and start asking questions. Great salespeople ask 4 questions before they show anything.",
        ],
        framework: {
          name: "The 4 Discovery Questions",
          points: [
            { h: "The Occasion", b: "'Is this for a specific occasion, or just something you've been wanting?'" },
            { h: "The Person", b: "'Is this for yourself, or a gift?' If gift: 'Tell me a little about them.'" },
            { h: "The Preference", b: "'What did you have in mind — something classic, something bold, something in between?'" },
            { h: "The Constraint", b: "'Any preference on colour / size / material / budget range?' (Budget last, casually.)" },
          ],
        },
        drill: {
          name: "The 4-Question Drill",
          desc: "For tomorrow's shift, ask all 4 questions to every serious walk-in BEFORE bringing out a product. Notice how much better your recommendations become.",
        },
      },
      {
        title: "Selling the outcome, not the feature.",
        body: [
          "Amateurs sell features (\"18-carat gold, 24 stones, made in Italy\"). Pros sell outcomes (\"This is the piece she'll reach for on every important evening for the next 20 years\"). Features are what the product IS. Outcomes are what the product DOES for the buyer's life.",
        ],
        scripts: {
          say: [
            "This watch is what people remember when they meet you — it opens conversations.",
            "This piece works for a boardroom on Monday and a dinner on Saturday. That's the point of it.",
            "This is the kind of bag that becomes her signature.",
          ],
          dont: [
            "This has 24 stones and Italian leather. (Just facts, no story.)",
            "It has 3 storage compartments. (So does every bag.)",
            "It's made of high-quality material. (Vague. Adds nothing.)",
          ],
        },
        drill: {
          name: "The Feature-to-Outcome Translation",
          desc: "Take the 10 features you say most often about your top-selling product. Rewrite each one as an outcome. Practise saying the outcomes out loud until they're natural.",
        },
      },
      {
        title: "Storytelling with product.",
        body: [
          "A price tag is a number. A story is a memory. When you attach a story to the product — the maker, the material, the moment someone wore it, the tradition behind it — the customer stops calculating and starts imagining.",
          "Every product in your store has a story. If you don't know it, ask your manager. If your manager doesn't know it, invent one that's true to the brand and true to the piece.",
        ],
        drill: {
          name: "The Story Bank",
          desc: "Build a written bank of one story per top-selling SKU. Practise telling each in under 60 seconds. Use them naturally in your conversations.",
        },
      },
      {
        title: "The three-option rule.",
        body: [
          "Never show one product and never show ten. Show three. One below their apparent budget, one at it, and one above it. Let them feel the range. Let them tell you where their comfort lies.",
          "Presenting three products also creates natural contrast — good vs better vs best — which is how humans naturally make decisions.",
        ],
        framework: {
          name: "The Three-Option Layout",
          points: [
            { h: "The Good", b: "The value option. Below their apparent budget. Sets a floor." },
            { h: "The Better", b: "The one you actually recommend. At their budget." },
            { h: "The Best", b: "The aspirational one. Above their budget. Anchors up the perceived value of 'the Better'." },
          ],
        },
        scorecard: [
          "I ask all 4 discovery questions before bringing out product.",
          "I can translate any feature to an outcome in 5 seconds.",
          "I always present 3 options, never 1 or 10.",
        ],
      },
    ],
  },
  {
    number: 6,
    title: "Objection Handling",
    tagline: "The 5 objections you'll hear every day.",
    icon: ShieldCheck,
    lessons: [
      {
        title: "'It's too expensive.'",
        body: [
          "This is the most common objection in retail. It's rarely about the actual price — it's about whether the buyer sees enough value to justify it. Your job is not to defend the price. Your job is to make the value obvious.",
        ],
        scripts: {
          say: [
            "I understand — may I show you why it's priced where it is?",
            "Compared to what, ma'am / sir? Let me put it side-by-side.",
            "This piece is designed to last 15+ years — it works out to less than ₹X a day.",
          ],
          dont: [
            "Yes, it's expensive — but we have discounts.",
            "You get what you pay for! (Sounds defensive.)",
            "Only ₹XX,XXX ma'am, not so much. (Never dismiss their concern.)",
          ],
        },
        drill: {
          name: "The Value-Rebuild Drill",
          desc: "Take your 3 top-selling products. Write 3 sentences for each that reframe price as value. Rehearse until natural.",
        },
      },
      {
        title: "'I'm just looking.'",
        body: [
          "This is not an objection — it's a defence mechanism. The customer is protecting themselves from a pushy salesperson. Your job is to disarm the defence, not fight it.",
        ],
        scripts: {
          say: [
            "Of course — please take your time. I'll be right here if anything catches your eye.",
            "Perfect. This is a great section to just wander. May I open any cases for you to see closer?",
            "Take your time. May I know what draws you to this collection?",
          ],
          dont: [
            "Sure, but let me know if you have any questions. (Weak, gives up.)",
            "Okay, I'll leave you alone. (You have now abandoned the sale.)",
            "But this one is very good — you should see! (Fighting the customer.)",
          ],
        },
      },
      {
        title: "'Let me think about it.'",
        body: [
          "This means one of three things: they want to check with someone, they want to check the price elsewhere, or they're not fully sold. Your job is to figure out which — respectfully.",
        ],
        scripts: {
          say: [
            "Absolutely — it's a big decision. May I ask what would help you decide?",
            "Of course. Would it help if I set this aside for 24 hours, no obligation?",
            "Take all the time you need. May I WhatsApp you a photo so you have it while you think?",
          ],
          dont: [
            "But we have a discount today only! (Pressure tactic. Kills trust.)",
            "This is the last piece! (If it's true, say once, softly. Never twice.)",
            "Okay, come back whenever. (You've given up on the follow-up.)",
          ],
        },
        drill: {
          name: "The Set-Aside Script",
          desc: "Learn one clean 'set aside for 24 hours' script and use it every time you hear 'let me think about it'. Track how many come back to complete the purchase.",
        },
      },
      {
        title: "'Let me ask my husband / wife / partner.'",
        body: [
          "This is one of the most common objections in Indian retail. Handled poorly, you lose the sale. Handled well, you can double your chance of a close.",
          "The trick is to help them present the case to their partner — not fight to close them alone in the store.",
        ],
        scripts: {
          say: [
            "Of course — this is a decision worth including them in. Would a WhatsApp photo help, with a small note about why it's special?",
            "Absolutely. May I set it aside for you both — perhaps you can visit together tomorrow?",
            "Sure. If it helps, I can send you 3 shortlisted options — you two can decide over dinner.",
          ],
          dont: [
            "But this is such a great deal — decide now!",
            "Ma'am, why do you need to ask him? (Never. Ever. Ever.)",
            "Okay bye. (Zero effort at retention.)",
          ],
        },
        scorecard: [
          "I have a memorised 2-line response for each of the 5 objections.",
          "I've stopped using 'discount only today' as an objection-killer.",
          "I use the 'set aside for 24 hours' close at least 3 times a week.",
        ],
      },
    ],
  },
  {
    number: 7,
    title: "The Close + The Ethical Upsell",
    tagline: "How to ask for the sale without being pushy.",
    icon: Target,
    lessons: [
      {
        title: "The 4 clean closes.",
        body: [
          "Closing is not a moment. It's a rhythm. If you've done discovery well and handled objections cleanly, the close should feel like the natural next step — not a push.",
        ],
        framework: {
          name: "The 4 Clean Closes",
          points: [
            { h: "The Assumptive Close", b: "'Shall I get this packed for you?' Assumes the yes." },
            { h: "The Choice Close", b: "'Would you prefer the gold or the silver?' Assumes the purchase, gives choice." },
            { h: "The Summary Close", b: "'So this ticks the three things you wanted — shall we go ahead?' Recaps the value." },
            { h: "The Silence Close", b: "State the price. Stop talking. Wait. Let them close themselves." },
          ],
        },
        drill: {
          name: "The 4-Close Rotation",
          desc: "For one week, use a different close each day. Track which one converts best for your product and customers.",
        },
      },
      {
        title: "The ethical upsell.",
        body: [
          "Upselling gets a bad name because most people do it wrong. Done wrong, it feels like manipulation. Done right, it feels like the salesperson genuinely wanted to serve the customer better.",
          "The test is simple: if the customer would thank you for the upsell 3 months later, it was ethical. If they'd regret it, it wasn't.",
        ],
        scripts: {
          say: [
            "One tier up gets you [specific meaningful benefit]. Would that be worth an extra ₹X for you?",
            "This model has [specific practical advantage] for how you described using it. Worth considering?",
            "For gifting, most people go a step up on this — it lands better. Would you like to see it?",
          ],
          dont: [
            "Take the bigger one, na! (Zero justification.)",
            "This one is better — buy this. (No reason given.)",
            "Only ₹5,000 more! (Never frame the upsell as small money — always as extra value.)",
          ],
        },
      },
      {
        title: "The cross-sell — companion products.",
        body: [
          "The customer has just said yes. This is the single best moment to cross-sell — but only to genuinely useful companion products. Bag: care spray. Watch: extra strap. Lingerie: care kit. Jewellery: cleaning solution.",
          "Cross-selling immediately after a yes converts at 3-5x the rate of any other moment in the transaction.",
        ],
        drill: {
          name: "The Companion Map",
          desc: "For your top 10 SKUs, write down 1-2 genuine companion products. Practise offering them in the 20 seconds after the customer says yes.",
        },
      },
      {
        title: "The second-visit close.",
        body: [
          "Not every sale closes on the first visit — especially not in luxury. The salespeople with the highest closing rates are the ones with a deliberate second-visit playbook.",
          "The secret: never let the customer leave without a reason to come back — a name, a phone number, a set-aside piece, a scheduled callback.",
        ],
        framework: {
          name: "The Second-Visit Playbook",
          points: [
            { h: "Set Aside", b: "Physically or digitally hold the piece for 24-72 hours." },
            { h: "WhatsApp Recap", b: "Send a clean photo and 2-line note the same evening." },
            { h: "Soft Follow-Up", b: "'Hi, just checking if you'd like me to hold it another day.'" },
            { h: "Invitation", b: "'A new piece came in — thought of you. No pressure — come by if free.'" },
          ],
        },
        scorecard: [
          "I know all 4 clean closes by heart and can pick the right one situationally.",
          "I've written a 2-line ethical upsell script for my top 10 SKUs.",
          "I follow up on every serious walk-in within 24 hours.",
        ],
      },
    ],
  },
  {
    number: 8,
    title: "Building a Book of Repeat Customers",
    tagline: "Where a real career begins.",
    icon: TrendingUp,
    lessons: [
      {
        title: "The 80/20 of a great sales career.",
        body: [
          "Ask any 10-year veteran salesperson where their income really comes from. Almost every one will say: from the 40-60 customers who have shopped with me for years, and the friends they've referred.",
          "Walk-ins pay your base. Your book pays your career.",
        ],
        drill: {
          name: "The First 10 in Your Book",
          desc: "Identify the 10 customers you've served in the last 3 months who are most likely to return. Write down their names, phone numbers, and what they bought. This is the beginning of your book.",
        },
      },
      {
        title: "WhatsApp follow-up etiquette in India.",
        body: [
          "WhatsApp is the single most powerful tool a luxury salesperson has in India. Used well, it turns one-time buyers into lifetime customers. Used badly, it gets you blocked in a week.",
          "The rule: follow up like a trusted advisor, never like a marketing SMS.",
        ],
        scripts: {
          say: [
            "Hi [Name], hope the ring is settling in — send me a photo when she wears it, if you'd like!",
            "Namaste [Name], we just received a new collection I thought you'd love. No pressure — pop in when you're around.",
            "Wishing you and your family a very happy Diwali. — [Name], [Store]",
          ],
          dont: [
            "HELLO SIR / MADAM. DISCOUNT SALE STARTED. VISIT NOW. (Never in caps. Never generic.)",
            "Sir I told you last month — please buy the watch. (Never nag.)",
            "Please repost / please share this to 10 friends. (Instant unfollow.)",
          ],
        },
        framework: {
          name: "The WhatsApp Cadence",
          points: [
            { h: "Same evening", b: "Thank-you + photo of what they bought / considered." },
            { h: "Day 7", b: "Care tip or reminder relevant to their purchase." },
            { h: "Day 30", b: "Personal note — 'how are you enjoying it?'" },
            { h: "Occasion", b: "Birthday / anniversary greeting (only if they've told you)." },
            { h: "New arrival", b: "Only when there's something genuinely for their taste." },
          ],
        },
      },
      {
        title: "Building your Little Black Book.",
        body: [
          "The Little Black Book is your personal customer database. It's not the store's CRM — it's yours, and it goes with you your whole career. In it, you record for every serious customer: name, phone, WhatsApp, occasion, product bought, budget range, notes about their family, style, preferences.",
          "The salespeople with the biggest books at year 5 are the same ones who started keeping notes on Week 1.",
        ],
        drill: {
          name: "Start Your Black Book",
          desc: "Buy a small notebook — nothing digital. Divide it into 4 sections: A-F, G-M, N-S, T-Z. Start adding customers today. Aim for 100 entries by end of year 1.",
        },
      },
      {
        title: "The referral ask.",
        body: [
          "A happy customer is 20 times more likely to refer someone if you ask, than if you don't. Yet 90% of salespeople never ask. That's the single biggest missed revenue opportunity in retail.",
          "The ask needs to be earned (they must genuinely be happy), specific (name a person type), and low-pressure.",
        ],
        scripts: {
          say: [
            "I'm so glad you love it. If you have a friend who'd appreciate this kind of piece — I'd love to serve them too.",
            "Thank you so much for choosing us. If anyone in your circle is shopping for an anniversary gift, please do send them my way.",
            "Would it be alright if I sent you a small note to share, if it's ever useful?",
          ],
          dont: [
            "Please tell 10 people about us. (Too many, too vague.)",
            "Refer someone and get ₹500 off! (Cheapens the relationship.)",
            "No thank-you, no ask. (You just left money on the table.)",
          ],
        },
        scorecard: [
          "I have started my Little Black Book and added 25+ customers.",
          "I have a personal WhatsApp follow-up cadence I actually use.",
          "I've asked for at least 3 referrals in the last month.",
        ],
      },
    ],
  },
];
